import React, { FC, ReactNode, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Link as ReactRouterLink } from "react-router-dom";

import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import {
	Box,
	Breadcrumbs,
	Link,
	Link as MuiLink,
	Tooltip,
	Typography,
} from "@mui/material";

import { SATOSLink } from "@/components/Application/SATOSLink";
import { Translate } from "@/components/Application/Translate";

import { ProcedureFolder } from "@/model/Procedure/ProcedureFolder";
import { ProcedureTraining } from "@/model/Procedure/ProcedureTraining";

import { useFetchActiveProcedure } from "@/service/ProcedureQueries";

/* ---------- Small helpers ---------- */

const truncateSx = {
	display: "inline-flex",
	alignItems: "center",
	maxWidth: { xs: 160, sm: 240, md: 360 },
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
	verticalAlign: "middle",
};

type CrumbLinkProps = {
	to?: string;
	children: ReactNode;
	current?: boolean;
	title?: string;
};

/** Internal links use SATOSLink; current page renders Typography. */
const CrumbLink: FC<CrumbLinkProps> = ({ to, children, current, title }) => {
	if (current || !to) {
		return (
			<Typography
				variant="body2"
				color="text.primary"
				sx={truncateSx}
				aria-current="page"
				title={title as string}
			>
				{children}
			</Typography>
		);
	}
	return (
		<SATOSLink to={to} style={{ textDecoration: "none" }}>
			<Typography
				variant="body2"
				color="text.secondary"
				sx={{ ...truncateSx, "&:hover": { textDecoration: "underline" } }}
				title={title as string}
			>
				{children}
			</Typography>
		</SATOSLink>
	);
};

// Uses MUI/ React-Router link to avoid hard reload (preserve procedure context)
const ProcedureSingleCrumbLink: FC<CrumbLinkProps> = ({
	to,
	children,
	current,
	title,
}) => {
	if (current || !to) {
		return (
			<Typography
				variant="body2"
				color="text.primary"
				sx={truncateSx}
				aria-current="page"
				title={title as string}
			>
				{children}
			</Typography>
		);
	}
	return (
		<Link
			component={ReactRouterLink}
			to={to}
			style={{ textDecoration: "none" }}
		>
			<Typography
				variant="body2"
				color="text.secondary"
				sx={{ ...truncateSx, "&:hover": { textDecoration: "underline" } }}
				title={title as string}
			>
				{children}
			</Typography>
		</Link>
	);
};

const AppBreadcrumbs: FC<{ children: ReactNode }> = ({ children }) => (
	<Breadcrumbs
		aria-label="breadcrumb"
		separator="›"
		maxItems={4}
		itemsBeforeCollapse={1}
		itemsAfterCollapse={1}
		sx={{ "& .MuiBreadcrumbs-ol": { alignItems: "center" } }}
	>
		<Tooltip title={<Translate>Home</Translate>} enterDelay={600}>
			<SATOSLink
				to="/"
				style={{ display: "inline-flex", alignItems: "center" }}
			>
				<HomeRoundedIcon fontSize="small" />
			</SATOSLink>
		</Tooltip>
		{children}
	</Breadcrumbs>
);

/* ---------- Public components (same names as before) ---------- */

const HomeBreadCrumb: FC = () => (
	<SATOSLink to="/" style={{ display: "inline-flex", alignItems: "center" }}>
		<HomeRoundedIcon fontSize="small" />
	</SATOSLink>
);

export const BreadCrumbsDesign: FC = () => (
	<AppBreadcrumbs>
		<CrumbLink to="/procedures">
			<Translate String="Breadcrumbs.Link.Procedures">Procedures</Translate>
		</CrumbLink>
	</AppBreadcrumbs>
);

interface IBreadCrumbProcedureSingle {
	trainingType: ProcedureTraining | undefined;
	procedureState: ProcedureFolder | undefined;
	parrentprocedureid: number | undefined;
}

export const BreadCrumbTraining: FC<IBreadCrumbProcedureSingle> = (props) => {
	if (!props.trainingType || !props.parrentprocedureid) return null;
	return (
		<CrumbLink to={`/procedure/${props.parrentprocedureid}`}>
			<Translate String="Breadcrumbs.Link.Training">
				Training, click to parent
			</Translate>
		</CrumbLink>
	);
};

export const BreadCrumbProcedureSingle: FC<IBreadCrumbProcedureSingle> = (
	props,
) => {
	const { isLoading, data: procedure } = useFetchActiveProcedure();

	if (isLoading || !procedure) {
		return (
			<AppBreadcrumbs>
				<CrumbLink to="/procedures">
					<Translate String="Breadcrumbs.Link.Procedures">Procedures</Translate>
				</CrumbLink>
				<Typography variant="body2" color="text.secondary" sx={truncateSx}>
					…
				</Typography>
			</AppBreadcrumbs>
		);
	}

	return (
		<AppBreadcrumbs>
			<ProcedureSingleCrumbLink to="/procedures">
				<Translate String="Breadcrumbs.Link.Procedures">Procedures</Translate>
			</ProcedureSingleCrumbLink>
			<CrumbLink
				to={`/procedure/${procedure.Id}`}
				current
				title={procedure.Name}
			>
				{procedure.Name}
			</CrumbLink>
			<BreadCrumbTraining {...props} />
		</AppBreadcrumbs>
	);
};

export const BreadCrumbsTasks: FC = (props) => (
	<AppBreadcrumbs>
		<CrumbLink to="/tasks">Tasks</CrumbLink>
		{props.children}
	</AppBreadcrumbs>
);

export const BreadCrumbsArchive: FC = () => {
	const { isLoading, data: procedure } = useFetchActiveProcedure();

	return (
		<Box sx={{ p: "1rem 2rem" }}>
			<AppBreadcrumbs>
				<CrumbLink to="/archive">
					<Translate String="Breadcrumbs.Link.Archive">Archive</Translate>
				</CrumbLink>
				{!isLoading && procedure && (
					<CrumbLink
						to={`/archive/procedure/${procedure.Id}`}
						current
						title={procedure.Name}
					>
						{procedure.Name}
					</CrumbLink>
				)}
			</AppBreadcrumbs>
		</Box>
	);
};

/** Generic wrapper with Home + fallback to “Procedures” if no children provided. */
const BreadCrumbs: FC<{ children?: ReactNode }> = (props) => (
	<AppBreadcrumbs>
		{props.children ?? (
			<MuiLink underline="hover" href="/procedures">
				<Translate String="Breadcrumbs.Link.Procedures">Procedures</Translate>
			</MuiLink>
		)}
	</AppBreadcrumbs>
);

export const BreadCrumbsCompetency: FC = () => {
	// placeholder for future detail breadcrumb
	return (
		<BreadCrumbs>
			<CrumbLink to="/competency/user">
				<Translate String="Breadcrumbs.Link.Competency">Competency</Translate>
			</CrumbLink>
		</BreadCrumbs>
	);
};

export const BreadCrumbsJobs: FC = (props) => (
	<BreadCrumbs>
		<CrumbLink to="/jobs/pdfarchive">
			<Translate String="Heading.BreadCrumbs.Jobs">Jobs</Translate>
		</CrumbLink>
		{props.children}
	</BreadCrumbs>
);

export const BreadCrumbsEmailJobs: FC = (props) => (
	<BreadCrumbsJobs>
		<CrumbLink to="/jobsEmail">
			<Translate String="Heading.BreadCrumbs.Jobs.Email">Email</Translate>
		</CrumbLink>
		{props.children}
	</BreadCrumbsJobs>
);

export const BreadCrumbsSettings: FC = () => {
	const location = useLocation();
	const parts = useMemo(() => {
		// Support both HashRouter and BrowserRouter
		const raw =
			location.hash && location.hash.startsWith("#/")
				? location.hash.replace("#/", "")
				: location.pathname.replace(/^\//, "");
		return raw
			.split("/")
			.filter(Boolean)
			.map((el) => el.charAt(0).toUpperCase() + el.slice(1));
	}, [location.hash, location.pathname]);

	return (
		<BreadCrumbs>
			{parts.map((el, i) => (
				<Typography
					key={`${el}-${i}`}
					color={i === parts.length - 1 ? "text.primary" : "text.secondary"}
					sx={truncateSx}
					aria-current={i === parts.length - 1 ? "page" : undefined}
				>
					{el}
				</Typography>
			))}
		</BreadCrumbs>
	);
};
